import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Paper,
  Button,
  Breadcrumbs,
  Typography,
  TextField,
  Container,
  Grid,
  Box,
  MenuItem,
  Checkbox,
} from "@mui/material";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useTheme } from "@emotion/react";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";
import DownloadIcon from "@mui/icons-material/Download";
import * as XLSX from "xlsx";
import Barcode from "react-barcode";
import { useReactToPrint } from "react-to-print";
import { getName } from "../Other/common";

import { TablePagination, IconButton, InputAdornment } from "@mui/material";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LoaderSpin from "../LoaderSpin";
import { BASE_URL } from "../../urlConfig/Url";

const PrintableBarcodes = React.forwardRef(({ barcodes }, ref) => (
  <div ref={ref}>
    {barcodes.map((barcode, index) => (
      <div key={index} style={{ margin: "20px", pageBreakAfter: "always" }}>
        <Barcode value={barcode.Barcode} />
        <p>Plant Code: {barcode.PlantCode}</p>
        <p>Asset Code: {barcode.AssetCode}</p>
        <p>Description: {barcode.Description}</p>
        <p>Category: {barcode.Category}</p>
        <p>Depreciation: {barcode.Depreciation}%</p>
      </div>
    ))}
  </div>
));

const GenratedBarPrint = () => {
  const theme = useTheme();
  const [plantCode, setPlantCode] = useState("");
  const [assetCode, setAssetCode] = useState("");
  const [fromDate, setFromDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [toDate, setToDate] = useState(new Date().toISOString().split("T")[0]);
  const [cName, setCName] = useState({ plantCodes: [], assetCodes: [] });
  const [formMode, setFormMode] = useState("initial");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedBarcodes, setSelectedBarcodes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState("");

  const printableRef = useRef();
  const handlePrint = useReactToPrint({
    content: () => printableRef.current,
  });

  useEffect(() => {
    fetchGeneratedBarcodes();
    fetchCompaniesName();
  }, []);

  const fetchGeneratedBarcodes = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(
        BASE_URL + "/api/transaction/getAllGenerateBarcode"
      );
      if (response.data.success) {
        setSearchResults(response.data.response);
      } else {
        toast.error("Failed to fetch generated barcodes");
      }
    } catch (error) {
      console.error("Error fetching generated barcodes:", error);
      toast.error("An error occurred while fetching generated barcodes");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCompaniesName = async () => {
    try {
      const response = await axios.post(
        BASE_URL + "/api/transaction/loadComboPlantAndAsset"
      );
      if (response.data.success) {
        setCName(response.data.response);
      }
    } catch (error) {
      toast.error("Error fetching company data:", error);
    }
  };

  const fetchAssetCodes = async (selectedPlantCode) => {
    try {
      const response = await axios.post(
        BASE_URL + "/api/transaction/searchByPlantCode",
        { plantCode: selectedPlantCode }
      );
      if (response.data.success) {
        setCName((prev) => ({
          ...prev,
          assetCodes: response.data.response.map((item) => item.AssetCode),
        }));
      }
    } catch (error) {
      toast.error("Error fetching asset codes:", error);
    }
  };

  const fetchFindBarcode = async () => {
    try {
      const response = await axios.post(
        BASE_URL + "/api/transaction/findGenrateBarcode",
        {
          plantCode: plantCode,
          assetCode: assetCode,
          frmDate: fromDate,
          toDate: toDate,
        }
      );
      if (response.data.success) {
        setSearchResults(response.data.response);
      } else {
        toast.error("Failed to fetch barcodes");
      }
    } catch (error) {
      toast.error("Error fetching barcode data:", error);
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  // const filteredResults = searchResults.filter((result) =>
  //   Object.values(result).some(
  //     (value) =>
  //       value &&
  //       value.toString().toLowerCase().includes(searchTerm.toLowerCase())
  //   )
  // );

  // const handleChangePage = (event, newPage) => {
  //   setPage(newPage);
  // };

  // const handleChangeRowsPerPage = (event) => {
  //   setRowsPerPage(parseInt(event.target.value, 10));
  //   setPage(0);
  // };

  const handleBarcodeSelect = (barcode) => {
    if (barcode) {
      setSelectedBarcodes((prev) => {
        if (prev.includes(barcode)) {
          return prev.filter((b) => b !== barcode);
        } else {
          return [...prev, barcode];
        }
      });
    }
  };

  const handleSelectAllBarcodes = (event) => {
    if (event.target.checked) {
      const currentPageBarcodes = filteredResults
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .filter((result) => result.Barcode) // Filter out null barcodes
        .map((result) => result.Barcode);
      setSelectedBarcodes((prev) => [
        ...new Set([...prev, ...currentPageBarcodes]),
      ]);
    } else {
      setSelectedBarcodes([]);
    }
  };

  const handleExportToExcel = () => {
    const dataToExport = searchResults.filter((result) =>
      selectedBarcodes.includes(result.Barcode)
    );

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Generated Barcodes");
    XLSX.writeFile(workbook, "GeneratedBarcodes.xlsx");
  };

  const handlePrintBarcodes = async () => {
    if (selectedBarcodes.length === 0) {
      toast.error("Please select barcodes to print");
      return;
    }

    const selectedBarcodeData = searchResults.filter((result) =>
      selectedBarcodes.includes(result.Barcode)
    );

    for (let i = 0; i < selectedBarcodeData.length; i++) {
      const barcode = selectedBarcodeData[i];
      const data = {
        uniqueCode: barcode.Barcode,
        user: getName().Name, // Assuming getName() returns an object with EmployeeName
        qrCodeImage: "data:image/png;base64," + barcode.Barcode, // You might need to adjust this based on how you're storing the barcode image
      };

      // console.log(data)
      try {
        await new Promise((resolve) => setTimeout(resolve, 200)); // 200ms delay
        const response = await axios.post(
          BASE_URL + "/api/transaction/updatePrintStatus",
          data
        );
        if (response.data.success) {
          //   toast.success(`Updated reprint flag for barcode: ${barcode.Barcode}`);
        } else {
          toast.error(
            `Failed to update reprint flag for barcode: ${barcode.Barcode}`
          );
        }
      } catch (error) {
        console.error("Error updating reprint flag:", error);
        toast.error(
          `Error updating reprint flag for barcode: ${barcode.Barcode}`
        );
      }
    }

    // After all API calls are done, trigger the print
    handlePrint();
    fetchGeneratedBarcodes();
  };

  /**
   * =======================Add these functions for pagination and search===============================
   */
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFirstPageButtonClick = () => {
    setPage(0);
  };

  const handleLastPageButtonClick = () => {
    setPage(Math.max(0, Math.ceil(filteredResults.length / rowsPerPage) - 1));
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  // const filteredResults = searchResults.filter((result) =>
  //   Object.values(result).some((value) =>
  //     value.toString().toLowerCase().includes(searchTerm.toLowerCase())
  //   )
  // );

  const filteredResults = searchResults.filter((result) =>
    Object.values(result).some(
      (value) =>
        value &&
        value.toString().toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const handlePlantCodeChange = (e) => {
    const selectedPlantCode = e.target.value;
    setPlantCode(selectedPlantCode);
    setAssetCode(""); // Reset asset code when plant code changes
    fetchAssetCodes(selectedPlantCode);
  };

  // useEffect(() => {
  //   console.log("Selected Barcodes:", selectedBarcodes);
  // }, [selectedBarcodes]);
  return (
    <Container maxWidth="lg">
      {/* Breadcrumbs and title */}
      <Box sx={{ mt: 1, mb: 2 }}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            to="/dashboard/dashbordhome"
            style={{
              textDecoration: "none",
              color: theme.palette.primary.main,
            }}
          >
            Home
          </Link>
          <Typography color="textPrimary">Generated Asset Barcode</Typography>
        </Breadcrumbs>
      </Box>

      {/* Search and filter form */}
      <Box sx={{ mt: 3, overflowX: "auto" }}>
        <Paper elevation={3} sx={{ padding: "2%" }}>
          <Typography variant="h6" mb={2} sx={{ textAlign: "left" }}>
            Generated Asset Barcode
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={10} sm={3}>
              <TextField
                select
                label="Plant Code"
                fullWidth
                size="small"
                value={plantCode}
                onChange={handlePlantCodeChange}
              >
                {cName.plantCodes.map((code) => (
                  <MenuItem key={code} value={code}>
                    {code}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={10} sm={3}>
              <TextField
                select
                label="Asset Code"
                fullWidth
                size="small"
                value={assetCode}
                onChange={(e) => setAssetCode(e.target.value)}
              >
                {cName.assetCodes.length === 0 ? (
                  <MenuItem value="" disabled>
                    No Asset Codes Available
                  </MenuItem>
                ) : (
                  cName.assetCodes.map((code) => (
                    <MenuItem key={code} value={code}>
                      {code}
                    </MenuItem>
                  ))
                )}
              </TextField>
            </Grid>
            <Grid item xs={10} sm={3}>
              <TextField
                type="date"
                label="From Date"
                fullWidth
                size="small"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={10} sm={3}>
              <TextField
                type="date"
                label="To Date"
                fullWidth
                size="small"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
          <Grid
            container
            justifyContent={{ sx: "center", sm: "flex-end" }}
            mt={5}
            spacing={1}
          >
            <Grid item>
              <Button
                variant="contained"
                size="small"
                onClick={fetchFindBarcode}
              >
                <AddIcon />
                Search
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  setPlantCode("");
                  setAssetCode("");
                  setFromDate("");
                  setToDate("");
                  fetchGeneratedBarcodes();
                }}
              >
                <CloseIcon />
                Cancel
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {/* Table */}
      <Box sx={{ mt: 3, overflowX: "auto" }}>
        <Paper elevation={3} sx={{ padding: "2%" }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <TextField
              size="small"
              placeholder="Search..."
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <LoaderSpin show={isLoading} />
          <div className="table-container">
            <table className="responsive-table" style={{ textAlign: "center" }}>
              <thead style={{ textAlign: "center" }}>
                <tr>
                  <th>
                    <Checkbox
                      style={{ color: "white" }}
                      checked={filteredResults
                        .slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage
                        )
                        .filter((result) => result.Barcode) // Filter out null barcodes
                        .every((result) =>
                          selectedBarcodes.includes(result.Barcode)
                        )}
                      indeterminate={
                        filteredResults
                          .slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage
                          )
                          .filter((result) => result.Barcode) // Filter out null barcodes
                          .some((result) =>
                            selectedBarcodes.includes(result.Barcode)
                          ) &&
                        !filteredResults
                          .slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage
                          )
                          .filter((result) => result.Barcode) // Filter out null barcodes
                          .every((result) =>
                            selectedBarcodes.includes(result.Barcode)
                          )
                      }
                      onChange={handleSelectAllBarcodes}
                    />
                  </th>
                  <th>No.</th>
                  <th>Barcode</th>
                  <th>Plant Code</th>
                  <th>Asset Code</th>
                  <th>Description</th>
                  <th>Category</th>
                  <th>Depreciation</th>
                  <th>Created By</th>
                  <th>Created Date</th>
                  <th>Preview</th>
                </tr>
              </thead>
              <tbody>
                {filteredResults
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((result, i) => (
                    <tr key={result.Barcode}>
                      <td>
                        <Checkbox
                          checked={
                            result.Barcode &&
                            selectedBarcodes.includes(result.Barcode)
                          }
                          onChange={() => handleBarcodeSelect(result.Barcode)}
                          disabled={!result.Barcode}
                        />
                      </td>
                      <td>{i + 1}</td>
                      <td data-label="Barcode">{result.Barcode}</td>
                      <td data-label="Plant Code">{result.PlantCode}</td>
                      <td data-label="Asset Code">{result.AssetCode}</td>
                      <td data-label="Description">{result.Description}</td>
                      <td data-label="Category">{result.Category}</td>
                      <td data-label="Depreciation">{result.Depreciation}%</td>
                      <td data-label="Created By">{result.CreatedBy}</td>
                      <td data-label="Created Date">
                        {new Date(result.CreatedDate).toLocaleDateString()}
                      </td>
                      <td data-label="Preview">
                        {result.Barcode ? (
                          <Barcode
                            value={result.Barcode}
                            width={1}
                            height={30}
                          />
                        ) : (
                          "No barcode available"
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredResults.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ActionsComponent={() => (
              <Box
                sx={{
                  flexShrink: 0,
                  ml: 2.5,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <IconButton
                  onClick={handleFirstPageButtonClick}
                  disabled={page === 0}
                  aria-label="first page"
                >
                  <FirstPageIcon />
                </IconButton>
                <IconButton
                  onClick={() => handleChangePage(null, page - 1)}
                  disabled={page === 0}
                  aria-label="previous page"
                >
                  <KeyboardArrowLeft />
                </IconButton>
                <IconButton
                  onClick={() => handleChangePage(null, page + 1)}
                  disabled={
                    page >= Math.ceil(filteredResults.length / rowsPerPage) - 1
                  }
                  aria-label="next page"
                >
                  <KeyboardArrowRight />
                </IconButton>
                <IconButton
                  onClick={handleLastPageButtonClick}
                  disabled={
                    page >= Math.ceil(filteredResults.length / rowsPerPage) - 1
                  }
                  aria-label="last page"
                >
                  <LastPageIcon />
                </IconButton>
              </Box>
            )}
          />
        </Paper>
      </Box>

      {/* Print and Export buttons */}
      <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<PrintIcon />}
          onClick={handlePrintBarcodes}
          disabled={selectedBarcodes.length === 0}
          sx={{ mr: 2 }}
        >
          Print Selected Barcodes
        </Button>
        {/* <Button
          variant="contained"
          color="secondary"
          startIcon={<DownloadIcon />}
          onClick={handleExportToExcel}
          disabled={selectedBarcodes.length === 0}
        >
          Export to Excel
        </Button> */}
      </Box>

      {/* Hidden printable content */}
      <div style={{ display: "none" }}>
        <PrintableBarcodes
          ref={printableRef}
          barcodes={searchResults.filter((result) =>
            selectedBarcodes.includes(result.Barcode)
          )}
        />
      </div>
    </Container>
  );
};

export default GenratedBarPrint;
